/**
 * El control por persona: lo que cada quien hizo con la plata, lado a lado.
 *
 * A void, a refund, a price brought down, a venta rápida, a write-off, a drawer short: each is
 * ordinary once, and none of them is wrong on its own. What gives somebody away is doing more of
 * them than everybody else, week after week — and nothing in the app put the people side by side.
 * The sale row does not even say who rang it: the event does, so this reads the log.
 *
 * It also lists the sales the reducer lets through by design (see core `permisos.ts`: a sale is
 * never refused, since the goods have left): another price list, quick sales past the limit, a
 * price below the margin floor or fiado past the limit, with no owner's yes on them — or a "yes"
 * from somebody who is not an owner.
 *
 * The name on an event, and the owner's yes on it, are the device's word (`AUDITORIAS.md` item 4).
 * So a sale counts for whoever was using its device when it was rung, as the device's own
 * sign-ins say, and one in somebody else's name is flagged. What went through with the owner's
 * yes is counted per person, since a yes typed by somebody who learned the PIN looks like any
 * other. And each sign-in with an owner's PIN is listed, device by device, for the owner to
 * recognise.
 *
 * Behind each card, its records (`registros`, 2026-10-23), for the person's own page: the sales it
 * counts, the voids and returns, and each count of a drawer, read by the same rules, so the page
 * lists what the card adds up.
 */

import { can, costoDeUnidadVenta, lineTotal, type Centavos, type Milli, type UnitCost } from '@abastece/shared';
import {
  ACCOUNTS,
  responsableDe,
  type Abono,
  type Cliente,
  type Compra,
  type Devolucion,
  type JournalEntry,
  type Pedido,
  type Salida,
  type SesionCaja,
  type Usuario,
  type Venta,
} from '@abastece/core';

/** The days «Control» looks back over: a week, or a month. */
export const DIAS_CONTROL = [7, 30] as const;
export type DiasControl = (typeof DIAS_CONTROL)[number];

/**
 * The days, in the address (2026-10-23): coming back from a person's page finds «Control» on the
 * days it was left on, and the person's page opens on the days their card was read over. A week is
 * left out, and anything else is a week.
 */
export function busquedaDeControl(s: Record<string, unknown>): { dias?: 30 } {
  return s['dias'] === 30 ? { dias: 30 } : {};
}

/** An event as the local log keeps it: what this needs of it. */
export interface EventoLog {
  id: string;
  type: string;
  payload?: unknown;
  actorId: string;
  clientTs: string;
  /** The device it came from and its place in that device's own order: who was using it then. */
  deviceId?: string;
  deviceSeq?: number;
}

export interface FilaPersona {
  usuarioId: string;
  ventas: { n: number; total: number };
  anuladas: { n: number; total: number };
  devueltas: { n: number; total: number };
  /** Lines sold below their list price, and by how much in all. */
  rebajas: { n: number; total: number };
  rapidas: { n: number; total: number };
  mermas: { n: number; costo: number };
  /** What counts found against this person: cortes, handovers, receipts. */
  caja: { faltante: number; sobrante: number };
  pinesMal: number;
  /** What went through with an owner's yes on it: sales, voids, refunds, abonos voided. */
  conSiDelDueno: { n: number; total: number };
}

/** `otroNombre`: rung in the name of somebody other than whoever was using the device. */
export type Motivo = 'lista' | 'rapidas' | 'margen' | 'fiado' | 'noEsDueno' | 'otroNombre';

/** An owner's PIN signing in on a device, in the period. */
export interface EntradaDueno {
  dispositivoId: string;
  usuarioId: string;
  n: number;
  /** The last one's `clientTs`. */
  ultima: string;
}

export interface VentaSinAutorizar {
  ventaId: string;
  ticket: string;
  vendedorId: string;
  fecha: string;
  motivos: Motivo[];
}

export interface Faltante {
  eventoId: string;
  sesionId: string;
  usuarioId: string;
  /** Positive: how much was missing, less what the owner has explained of it. */
  monto: number;
  /** What the owner explained of it (`faltante.explicado`). */
  explicado: number;
  cuando: string;
}

/** A till's drawer counted: as it opened, changed hands, closed, or as the owner received its money. */
export type ConteoDeCaja = 'apertura' | 'entrega' | 'corte' | 'recibido';

/**
 * A count, on the page of whoever counted and of whoever had the drawer, which are the same person
 * but at a handover, at a corte of somebody else's till and when the owner receives a till's money.
 */
export interface RegistroConteo {
  tipo: 'conteo';
  conteo: ConteoDeCaja;
  eventoId: string;
  sesionId: string;
  cuando: string;
  /** Who counted. */
  hizo: string;
  /** Whose the drawer was: what the count found is theirs, as their card says. */
  deQuien: string;
  /** What it found, positive over and negative short, as the owner corrected it if they did. */
  diferencia: number;
  /** The owner corrected it (`conteosCorregidos`). */
  corregido: boolean;
  /** What the owner explained of a shortage (`faltante.explicado`). */
  explicado: number;
  /**
   * What is still unexplained of it, as «Faltantes de caja» lists it: less what a purchase's cost
   * corrected since explains. An opening's is nobody's on the cards, so it is never here.
   */
  porExplicar: number;
}

/**
 * What a person did, for their own page (the owner, 2026-10-23: «click the name of the user and see
 * a list of their till openings/closings and sales»): the sales counted on their card, the voids
 * and returns they made, and every count of a drawer they counted or had.
 */
export type Registro =
  | { tipo: 'venta'; eventoId: string; ventaId: string; cuando: string; motivos: Motivo[] }
  | { tipo: 'anulacion'; eventoId: string; ventaId: string; cuando: string }
  | { tipo: 'devolucion'; eventoId: string; devolucionId: string; ventaId: string; cuando: string }
  | RegistroConteo;

export interface Control {
  personas: FilaPersona[];
  sinAutorizar: VentaSinAutorizar[];
  faltantes: Faltante[];
  entradasDueno: EntradaDueno[];
  /**
   * Each person's records, in log order. Kept apart from the cards: an owner who only received a
   * till's money, or somebody who has only opened a till, has records here and no card.
   */
  registros: ReadonlyMap<string, readonly Registro[]>;
}

export interface DatosControl {
  /** The period's events, in log order. */
  eventos: readonly EventoLog[];
  /**
   * Every handover ever, in log order: whose a drawer was at a handover depends on the ones
   * before it, whatever the period.
   */
  entregas: readonly EventoLog[];
  /**
   * Every device's registration and every sign-in ever: who was using a device when it recorded
   * something is its last one before, whatever the period. Whoever registered it until then.
   */
  entradas?: readonly EventoLog[];
  /** The abonos voided in the period, for how much each was. */
  abonos?: ReadonlyMap<string, Abono>;
  ventas: ReadonlyMap<string, Venta>;
  /**
   * The sales standing in the books, of those in the period: a record the server turned away (a
   * till that sold what another had just sold, `soloSiHay`) stands for no sale. Without it, every
   * record counts.
   */
  enPie?: ReadonlySet<string>;
  sesiones: ReadonlyMap<string, SesionCaja>;
  /**
   * Every entry each count or correction wrote, by its event. All of them: a corte that hands the
   * money over writes the count's difference in one and the handover in another.
   */
  asientos: ReadonlyMap<string, readonly JournalEntry[]>;
  devoluciones: ReadonlyMap<string, Devolucion>;
  salidas: ReadonlyMap<string, Salida>;
  /** The purchases a cost correction touched: paid out of a drawer, one may explain its count. */
  compras: ReadonlyMap<string, Compra>;
  /**
   * What the owner explained of each count's shortage, by the count (`faltante.explicado`): money
   * that went on something nobody wrote down, and is not missing. Explanations voided since are
   * not in it.
   */
  explicados?: ReadonlyMap<string, number>;
  /**
   * What a till's count found once the owner corrected it (`conteosCorregidos`), by the count: its
   * own entries say what it found as it was typed.
   */
  corregidos?: Readonly<Record<string, number>>;
  clientes: ReadonlyMap<string, Cliente>;
  /**
   * The orders the period's sales delivered: a sale at its order's list, or a line at the list its
   * order put it at (2026-10-18), is the order's doing, not whoever rings it up.
   */
  pedidos?: ReadonlyMap<string, Pedido>;
  usuarios: readonly Usuario[];
  listaPredeterminadaId: string | null;
  ventaRapidaMaxima: number;
  margenMinimoBp: number;
}

interface LineaEvento {
  productoId: string | null;
  unidadVentaId?: string | null;
  cantidad: Milli;
  factor: Milli;
  precioLista: Centavos;
  /** The line's own list, when it is not the sale's (2026-10-11). */
  listaId?: string;
  precioUnitario: Centavos;
  autorizadoPor?: string;
  costoReferencia?: UnitCost;
  subtotal: Centavos;
}

interface PagoEvento {
  forma: string;
  monto: number;
  autorizadoPor?: string;
}

interface VentaEvento {
  ventaId: string;
  ticket: string;
  fecha: string;
  listaId: string;
  listaAutorizadaPor?: string;
  apartadoAutorizadoPor?: string;
  faltaAutorizadaPor?: string;
  clienteId: string | null;
  /** The order it delivers. */
  pedidoId?: string | null;
  total: number;
  lineas: LineaEvento[];
  pagos: PagoEvento[];
}

/** What a count posted over (positive) or short (negative): the entry's lines on those accounts. */
function diferenciaDe(asientos: readonly JournalEntry[] | undefined): number {
  let d = 0;
  for (const asiento of asientos ?? []) {
    for (const l of asiento.lineas) {
      if (l.cuenta === ACCOUNTS.SOBRANTE_CAJA.code) d += l.haber - l.debe;
      if (l.cuenta === ACCOUNTS.FALTANTE_CAJA.code) d -= l.debe - l.haber;
    }
  }
  return d;
}

function margenBajo(l: LineaEvento, minimoBp: number): boolean {
  if (l.precioUnitario >= l.precioLista || !l.costoReferencia) return false;
  const costo = costoDeUnidadVenta(l.costoReferencia, l.factor);
  if (costo <= 0 || l.precioUnitario <= 0) return false;
  return Math.round(((l.precioUnitario - costo) / l.precioUnitario) * 10_000) < minimoBp;
}

export function controlPorPersona(d: DatosControl): Control {
  const filas = new Map<string, FilaPersona>();
  const fila = (id: string): FilaPersona => {
    let f = filas.get(id);
    if (!f) {
      f = {
        usuarioId: id,
        ventas: { n: 0, total: 0 },
        anuladas: { n: 0, total: 0 },
        devueltas: { n: 0, total: 0 },
        rebajas: { n: 0, total: 0 },
        rapidas: { n: 0, total: 0 },
        mermas: { n: 0, costo: 0 },
        caja: { faltante: 0, sobrante: 0 },
        pinesMal: 0,
        conSiDelDueno: { n: 0, total: 0 },
      };
      filas.set(id, f);
    }
    return f;
  };
  const duenos = new Set(d.usuarios.filter((u) => u.rol === 'dueno').map((u) => u.id));
  const esDueno = (id: string | undefined) => id !== undefined && duenos.has(id);
  /** Who charges another list without the owner's PIN (`cobrar_otra_lista`): a cashier since 2026-10-15, not a driver. */
  const rolDe = new Map(d.usuarios.map((u) => [u.id, u.rol]));
  const cobraOtraLista = (id: string) => {
    const rol = rolDe.get(id);
    return rol !== undefined && can(rol, 'cobrar_otra_lista');
  };
  const sinAutorizar: VentaSinAutorizar[] = [];
  const faltantes: Faltante[] = [];
  const entradasDueno = new Map<string, EntradaDueno>();
  const registros = new Map<string, Registro[]>();
  const anotar = (quien: string, r: Registro) => {
    const lista = registros.get(quien);
    if (lista) lista.push(r);
    else registros.set(quien, [r]);
  };
  /** Each count's shortage on the cards, to say on its record what is left of it once the log is read. */
  const sinExplicar: [RegistroConteo, Faltante][] = [];
  /** A count, on both pages it is about. */
  const anotarConteo = (
    e: EventoLog,
    conteo: ConteoDeCaja,
    sesionId: string,
    deQuien: string,
    hallado: { diferencia: number; explicado: number; faltante?: Faltante },
  ) => {
    const r: RegistroConteo = {
      tipo: 'conteo',
      conteo,
      eventoId: e.id,
      sesionId,
      cuando: e.clientTs,
      hizo: e.actorId,
      deQuien,
      diferencia: hallado.diferencia,
      corregido: d.corregidos?.[e.id] !== undefined,
      explicado: hallado.explicado,
      porExplicar: 0,
    };
    if (hallado.faltante) sinExplicar.push([r, hallado.faltante]);
    anotar(e.actorId, r);
    if (deQuien !== e.actorId) anotar(deQuien, r);
  };

  /** Per device, in the device's own order: from each event on, who was using it. */
  const usos = new Map<string, { seq: number; usuarioId: string }[]>();
  for (const e of d.entradas ?? []) {
    if (!e.deviceId || e.deviceSeq === undefined) continue;
    const lista = usos.get(e.deviceId) ?? [];
    lista.push({ seq: e.deviceSeq, usuarioId: e.actorId });
    usos.set(e.deviceId, lista);
  }
  for (const lista of usos.values()) lista.sort((a, b) => a.seq - b.seq);
  /** Who was using the device an event came from when it was recorded. Undefined: no record. */
  const quienUsaba = (e: EventoLog): string | undefined => {
    const lista = e.deviceId && e.deviceSeq !== undefined ? usos.get(e.deviceId) : undefined;
    if (!lista) return undefined;
    let quien: string | undefined;
    for (const u of lista) {
      if (u.seq >= e.deviceSeq!) break;
      quien = u.usuarioId;
    }
    return quien;
  };
  /** An undoing that carried an owner's yes, on whoever did it. */
  const conSi = (e: EventoLog, monto: number | undefined) => {
    if (!esDueno((e.payload as { autorizadoPor?: string }).autorizadoPor) || esDueno(e.actorId)) return;
    const f = fila(e.actorId);
    f.conSiDelDueno.n += 1;
    f.conSiDelDueno.total += monto ?? 0;
  };

  /** What a count found, as the owner corrected it, and what the owner explained of a shortage. */
  const hallado = (e: EventoLog) => {
    const diferencia = d.corregidos?.[e.id] ?? diferenciaDe(d.asientos.get(e.id));
    return { diferencia, explicado: diferencia < 0 ? Math.min(-diferencia, d.explicados?.get(e.id) ?? 0) : 0 };
  };

  /** A count's difference, on whoever had the drawer: of a shortage, what nobody has explained. */
  const contar = (e: EventoLog, quien: string, sesionId: string): { diferencia: number; explicado: number; faltante?: Faltante } => {
    const h = hallado(e);
    const dif = h.diferencia;
    if (dif < 0) {
      if (h.explicado === -dif) return h;
      fila(quien).caja.faltante += -dif - h.explicado;
      const faltante = { eventoId: e.id, sesionId, usuarioId: quien, monto: -dif - h.explicado, explicado: h.explicado, cuando: e.clientTs };
      faltantes.push(faltante);
      return { ...h, faltante };
    } else if (dif > 0) {
      fila(quien).caja.sobrante += dif;
    }
    return h;
  };

  /**
   * A purchase paid out of a drawer whose cost was corrected once the drawer had been counted:
   * the count's difference was that cost, not whoever had the drawer. What it explains comes off
   * that person, and off the drawer's latest shortfall before it.
   */
  const explicar = (e: EventoLog) => {
    const compra = d.compras.get((e.payload as { compraId: string }).compraId);
    const sesion = compra?.sesionId ? d.sesiones.get(compra.sesionId) : undefined;
    const dif = diferenciaDe(d.asientos.get(e.id));
    if (!sesion || dif === 0) return;
    const f = fila(responsableDe(sesion));
    if (dif < 0) {
      f.caja.sobrante = Math.max(0, f.caja.sobrante + dif);
      return;
    }
    f.caja.faltante = Math.max(0, f.caja.faltante - dif);
    let queda = dif;
    for (let i = faltantes.length - 1; i >= 0 && queda > 0; i--) {
      const x = faltantes[i]!;
      if (x.sesionId !== sesion.id) continue;
      const quita = Math.min(x.monto, queda);
      x.monto -= quita;
      queda -= quita;
      if (x.monto === 0) faltantes.splice(i, 1);
    }
  };

  // Whose each drawer was at each handover: whoever opened it, then whoever received it last.
  const deQuienEra = new Map<string, string>();
  const porCaja = new Map<string, string>();
  for (const e of d.entregas) {
    const { sesionId } = e.payload as { sesionId: string };
    const sesion = d.sesiones.get(sesionId);
    if (!sesion) continue;
    const antes = porCaja.get(sesionId) ?? sesion.usuarioId;
    if (antes === e.actorId) continue; // Refused by the reducer: already theirs.
    deQuienEra.set(e.id, antes);
    porCaja.set(sesionId, e.actorId);
  }

  /** Sales already counted: the copy of one the till never heard back about (`respaldo`) is the same sale. */
  const contadas = new Set<string>();
  for (const e of d.eventos) {
    switch (e.type) {
      case 'venta.registrada': {
        const p = e.payload as VentaEvento;
        if (contadas.has(p.ventaId) || (d.enPie && !d.enPie.has(p.ventaId))) break;
        contadas.add(p.ventaId);
        // Whoever was using the device rang it, whatever name the sale carries.
        const usaba = quienUsaba(e);
        const otroNombre = usaba !== undefined && usaba !== e.actorId;
        const quien = usaba ?? e.actorId;
        const f = fila(quien);
        f.ventas.n += 1;
        f.ventas.total += p.total;
        const registro: Registro = { tipo: 'venta', eventoId: e.id, ventaId: p.ventaId, cuando: e.clientTs, motivos: [] };
        anotar(quien, registro);
        let rapidasSinDueno = 0;
        let bajoMargen = false;
        for (const l of p.lineas) {
          if (l.productoId === null) {
            f.rapidas.n += 1;
            f.rapidas.total += l.subtotal;
            if (!esDueno(l.autorizadoPor)) rapidasSinDueno += lineTotal(l.precioUnitario, l.cantidad);
          }
          if (l.precioUnitario < l.precioLista) {
            f.rebajas.n += 1;
            f.rebajas.total += lineTotal(l.precioLista, l.cantidad) - lineTotal(l.precioUnitario, l.cantidad);
            if (margenBajo(l, d.margenMinimoBp) && !esDueno(l.autorizadoPor)) bajoMargen = true;
          }
        }
        const autorizaciones = [
          p.listaAutorizadaPor,
          p.apartadoAutorizadoPor,
          p.faltaAutorizadaPor,
          ...p.lineas.map((l) => l.autorizadoPor),
          ...p.pagos.map((x) => x.autorizadoPor),
        ].filter((x): x is string => x !== undefined);
        // The owner selling needs nobody's yes; in the owner's name on somebody else's session, it does.
        if (esDueno(quien) && !otroNombre) break;
        if (autorizaciones.some(esDueno)) {
          f.conSiDelDueno.n += 1;
          f.conSiDelDueno.total += p.total;
        }
        const motivos: Motivo[] = [];
        const cliente = p.clienteId ? d.clientes.get(p.clienteId) : undefined;
        const propia = cliente?.listaId ?? d.listaPredeterminadaId;
        const pedido = p.pedidoId ? d.pedidos?.get(p.pedidoId) : undefined;
        /** A line at the list the order it delivers put it at (2026-10-18). */
        const comoElPedido = (l: LineaEvento) =>
          pedido?.lineas.some((x) => x.unidadVentaId === l.unidadVentaId && (x.listaId ?? pedido.listaId) === l.listaId) ?? false;
        // The sale's list, or any line's own (a till chooses each line's since 2026-10-11), but where
        // the order it delivers set it, as the till takes the order's list for the sale's own.
        const otraLista =
          (p.listaId !== propia && p.listaId !== pedido?.listaId) ||
          p.lineas.some((l) => l.listaId !== undefined && l.listaId !== propia && !comoElPedido(l));
        if (propia !== null && otraLista && !cobraOtraLista(quien) && !esDueno(p.listaAutorizadaPor)) motivos.push('lista');
        if (rapidasSinDueno > d.ventaRapidaMaxima) motivos.push('rapidas');
        if (bajoMargen) motivos.push('margen');
        const fiado = p.pagos.filter((x) => x.forma === 'credito');
        const fiadoSinDueno = fiado.filter((x) => !esDueno(x.autorizadoPor)).reduce((a, x) => a + x.monto, 0);
        // Checked against the limit as it stands: what the client owed that day is not on record here.
        if (fiadoSinDueno > 0 && (cliente?.limiteCredito == null || fiadoSinDueno > cliente.limiteCredito)) {
          motivos.push('fiado');
        }
        if (autorizaciones.some((a) => !esDueno(a))) motivos.push('noEsDueno');
        if (otroNombre) motivos.push('otroNombre');
        if (motivos.length > 0) {
          registro.motivos = motivos;
          sinAutorizar.push({ ventaId: p.ventaId, ticket: p.ticket, vendedorId: quien, fecha: p.fecha, motivos });
        }
        break;
      }
      case 'venta.anulada': {
        const { ventaId } = e.payload as { ventaId: string };
        const venta = d.ventas.get(ventaId);
        const f = fila(e.actorId);
        f.anuladas.n += 1;
        f.anuladas.total += venta?.total ?? 0;
        conSi(e, venta?.total);
        anotar(e.actorId, { tipo: 'anulacion', eventoId: e.id, ventaId, cuando: e.clientTs });
        break;
      }
      case 'devolucion.registrada': {
        const { devolucionId, ventaId } = e.payload as { devolucionId: string; ventaId: string };
        const dev = d.devoluciones.get(devolucionId);
        const f = fila(e.actorId);
        f.devueltas.n += 1;
        f.devueltas.total += dev?.total ?? 0;
        conSi(e, dev?.total);
        anotar(e.actorId, { tipo: 'devolucion', eventoId: e.id, devolucionId, ventaId, cuando: e.clientTs });
        break;
      }
      case 'abono.anulado':
        conSi(e, d.abonos?.get((e.payload as { abonoId: string }).abonoId)?.monto);
        break;
      case 'sesion.iniciada': {
        if (!esDueno(e.actorId) || !e.deviceId) break;
        const clave = `${e.deviceId} ${e.actorId}`;
        const x = entradasDueno.get(clave) ?? { dispositivoId: e.deviceId, usuarioId: e.actorId, n: 0, ultima: e.clientTs };
        x.n += 1;
        if (e.clientTs > x.ultima) x.ultima = e.clientTs;
        entradasDueno.set(clave, x);
        break;
      }
      case 'inventario.salida': {
        const s = d.salidas.get((e.payload as { salidaId: string }).salidaId);
        const f = fila(e.actorId);
        f.mermas.n += 1;
        f.mermas.costo += s?.costo ?? 0;
        break;
      }
      case 'caja.cerrada':
      case 'efectivo.recibido': {
        // The corte and a receipt measure whoever had the drawer last.
        const sesion = d.sesiones.get((e.payload as { sesionId: string }).sesionId);
        if (!sesion) break;
        const quien = responsableDe(sesion);
        anotarConteo(e, e.type === 'caja.cerrada' ? 'corte' : 'recibido', sesion.id, quien, contar(e, quien, sesion.id));
        break;
      }
      case 'caja.entregada': {
        const antes = deQuienEra.get(e.id);
        const { sesionId } = e.payload as { sesionId: string };
        if (antes) anotarConteo(e, 'entrega', sesionId, antes, contar(e, antes, sesionId));
        break;
      }
      case 'caja.abierta':
        // On nobody's card: what an opening finds is between it and the corte before. Its own record.
        anotarConteo(e, 'apertura', (e.payload as { sesionId: string }).sesionId, e.actorId, hallado(e));
        break;
      case 'compra.costo_corregido':
        explicar(e);
        break;
      case 'sesion.pin_incorrecto':
        fila(e.actorId).pinesMal += 1;
        break;
    }
  }

  // A purchase's cost corrected later in the log takes off what was left of a shortage.
  for (const [r, f] of sinExplicar) r.porExplicar = f.monto;

  const orden = new Map(d.usuarios.map((u, i) => [u.id, i]));
  return {
    personas: [...filas.values()].sort(
      (a, b) => (orden.get(a.usuarioId) ?? 1e9) - (orden.get(b.usuarioId) ?? 1e9),
    ),
    sinAutorizar,
    faltantes,
    entradasDueno: [...entradasDueno.values()].sort((a, b) => b.ultima.localeCompare(a.ultima)),
    registros,
  };
}
